/* External */
import { useEffect, useRef, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

/* Ott-common */
import { getSourceObjectArray } from "ott-common/dist/playerUtils";

/* Actions */
import { setPlayerErrorState } from "../state/player/actions";
import { pulseStreamHeartbeat } from "../state/player/heartbeat/actions";

/* Others */
import { generateRandomString, checkNetworkBlackout, STATUS_TYPES } from "./misc";
import { convivaTraceLogs, isTouchstoneEnabled, isProduction, isBrazil } from "./env";

const ERROR_CODE_EXCEEDED_STREAM_LIMITS_BY_STATION = "100";
const ERROR_CODE_EXCEEDED_STREAM_LIMITS = "101";
const ERROR_CODE_NO_ENTITLED = "1404";
const UNRECOVERABLE_ERROR_CODE_HDCP = 2006; // name: "DRM_NO_KEY_SYSTEM"

export const HEARTBEAT_ASSET_TYPES = {
  VOD: "VOD",
  EPG: "EPG"
};

export const HEARTBEAT_PULSE_TYPES = {
  SEND: "SEND",
  STOP: "STOP"
};

export const PLAYER_ERROR_TYPE = {
  CONCURRENCY: "CONCURRENCY",
  ENTITLEMENTS_LIVE: "ENTITLEMENTS_LIVE",
  ENTITLEMENTS_VOD: "ENTITLEMENTS_VOD",
  HDCP: "HDCP",
  BLACKOUT: "BLACKOUT",
  NETWORK_BLACKOUT: "NETWORK_BLACKOUT",
  GENERIC: "GENERIC"
};

export const getPlaybackUrl = (urlsObject) => {
  const { dash, hls } = urlsObject || {};

  return dash?.primary || dash?.backup || hls?.primary || hls?.backup;
};

export const getLiveAssetDuration = (endTime, startTime) =>
  endTime && startTime ? (new Date(endTime) - new Date(startTime)) / 1000 : undefined;

export const useLiveEdgeProgress = (startTime) => {
  const [liveEdgeProgress, setLiveEdgeProgress] = useState();
  const intervalRef = useRef();

  useEffect(() => {
    if (!startTime) return;

    const updateLiveEdgeProgress = () => {
      const startTimeDate = new Date(startTime);
      const now = new Date();
      const newLiveEdgeProgress = Math.floor((now - startTimeDate) / 1000);

      if (newLiveEdgeProgress > 0) setLiveEdgeProgress(newLiveEdgeProgress);
    };

    intervalRef.current = setInterval(updateLiveEdgeProgress, 500);
    updateLiveEdgeProgress();

    return () => clearInterval(intervalRef.current);
  }, [startTime]);

  return liveEdgeProgress;
};

export const usePlaybackSources = (assetId, isLive) => {
  const liveUrls = usePlayerSelector("asset", "liveURLs");
  const vodUrls = usePlayerSelector("asset", "vodURLs");
  const entitlementsToken = usePlayerSelector("entitlementsToken");
  const prevEntitlementsToken = useRef();
  const [sources, setSources] = useState([]);
  const originalSources = isLive ? liveUrls : vodUrls;

  useEffect(() => {
    if (prevEntitlementsToken.current === entitlementsToken) return;

    prevEntitlementsToken.current = entitlementsToken;

    setSources(getSourceObjectArray(originalSources, entitlementsToken));
  }, [assetId, originalSources, entitlementsToken]);

  return sources;
};

export const usePlayerSelector = (subState, attribute) =>
  useSelector(
    ({
      player: {
        [subState]: { data }
      }
    }) => {
      if (!data) return;

      const dataToReturn = Array.isArray(data) ? data[0] : data;

      return attribute ? dataToReturn[attribute] : dataToReturn;
    }
  );

export const useSavedProgress = (assetId, isLive) => {
  const [savedProgress, setSavedProgress] = useState();
  const assetDuration = useSelector((state) => state.player.asset.data?.duration);
  const continueWatchingData = useSelector((state) => state.pdp.continueWatching.data);
  const history = useHistory();
  const location = useLocation();
  const streamsHistory = useSelector((state) => state.streamsHistory.data);
  const progressFromLocationState = location.state?.progress;
  const pathname = location.pathname;

  useEffect(() => {
    if (isLive || savedProgress) return;

    if (progressFromLocationState) {
      setSavedProgress(progressFromLocationState);
      history.replace(pathname);

      return;
    }

    let newSavedProgress =
      continueWatchingData?.id === assetId
        ? continueWatchingData.progress
        : streamsHistory?.find((streamHistory) => streamHistory.assetId === assetId)?.progress;

    if (newSavedProgress / assetDuration > 0.95) newSavedProgress = undefined;

    setSavedProgress(newSavedProgress);
  }, [
    assetDuration,
    assetId,
    continueWatchingData,
    history,
    isLive,
    pathname,
    progressFromLocationState,
    savedProgress,
    streamsHistory
  ]);

  return savedProgress;
};

/**
 * @param assetId {string} - from props
 * @param isLive {boolean} - from props
 * @param stopPulse {function}
 *
 * @return playerError {hook-string} - Empty string if no error found, any PLAYER_ERROR_TYPE otherwise
 * @return handleOnError {function} - Callback to be used in bitmovin component
 * @return handleHDCPError {function} - Callback to be used in bitmovin component
 */
export const useGetError = (isLive, stopPulse) => {
  const dispatch = useDispatch();
  const contentStatus = useSelector(({ player }) => player?.content?.status);
  const contentBlackout = useSelector(({ player }) => player?.content?.data?.[0]?.blackout) || false;
  const contentLabels = useSelector(({ player }) => player?.content?.data?.[0]?.labels) || [];
  const entitlementsTokenError = useSelector(({ player }) => player?.entitlementsToken?.error);
  const entitlementsTokenStatus = useSelector(({ player }) => player?.entitlementsToken?.status);
  const iso2Country = useSelector((state) => state.user?.account?.profile?.iso2country) || "";
  const [playerError, setPlayerErrorHook] = useState("");
  const setPlayerError = useCallback(
    (value) => {
      dispatch(setPlayerErrorState(value));
      setPlayerErrorHook(value);
      if (value) stopPulse(); // send stop heartbeat on crash
    },
    [dispatch, setPlayerErrorHook, stopPulse]
  );

  const handleUnrecoverableError = (error) => {
    console.error("player unrecoverable Error:", error);
    if (error?.code === UNRECOVERABLE_ERROR_CODE_HDCP) setPlayerError(PLAYER_ERROR_TYPE.HDCP);
    else setPlayerError(PLAYER_ERROR_TYPE.GENERIC);
  };

  const handleHDCPError = () => setPlayerError(PLAYER_ERROR_TYPE.HDCP);

  /**
   * Pops generic error when exhausts all sources and still throw errors
   * @params error {Error} - param from NonPlayableSources
   * @params sources {array}
   * @params sourcesIndex {number}
   */
  const handleSourceNotFoundError = (error, sources, sourcesIndex) => {
    console.error("player source Error:", error, "\nSource:", sources, sourcesIndex);
    if (!sources || sources.length - 1 <= sourcesIndex) {
      setPlayerError(PLAYER_ERROR_TYPE.GENERIC);
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (
      (contentStatus === STATUS_TYPES.FAILED_STATE || entitlementsTokenStatus === STATUS_TYPES.FAILED_STATE) &&
      !contentBlackout
    )
      setPlayerError(PLAYER_ERROR_TYPE.GENERIC);
    if (
      entitlementsTokenStatus === STATUS_TYPES.FAILED_STATE &&
      entitlementsTokenError.response.data?.code === ERROR_CODE_NO_ENTITLED &&
      isLive
    )
      setPlayerError(PLAYER_ERROR_TYPE.ENTITLEMENTS_LIVE);
    if (
      entitlementsTokenStatus === STATUS_TYPES.FAILED_STATE &&
      entitlementsTokenError.response.data?.code === ERROR_CODE_NO_ENTITLED &&
      !isLive
    )
      setPlayerError(PLAYER_ERROR_TYPE.ENTITLEMENTS_VOD);
    if (
      entitlementsTokenStatus === STATUS_TYPES.FAILED_STATE &&
      (entitlementsTokenError.response.data?.code === ERROR_CODE_EXCEEDED_STREAM_LIMITS ||
        entitlementsTokenError.response.data?.code === ERROR_CODE_EXCEEDED_STREAM_LIMITS_BY_STATION)
    )
      setPlayerError(PLAYER_ERROR_TYPE.CONCURRENCY);
    if (contentBlackout) setPlayerError(PLAYER_ERROR_TYPE.BLACKOUT);
    if (checkNetworkBlackout(contentLabels, iso2Country)) setPlayerError(PLAYER_ERROR_TYPE.NETWORK_BLACKOUT);
  }, [
    contentBlackout,
    contentLabels,
    contentStatus,
    entitlementsTokenError,
    entitlementsTokenStatus,
    isLive,
    iso2Country,
    playerError,
    setPlayerError
  ]);
  return {
    playerError,
    handleHDCPError,
    handleUnrecoverableError,
    handleSourceNotFoundError,
    setPlayerError
  };
};

export const useStreamHeartbeat = (isLive, isPlaying, isReadyToPlay, progress) => {
  const dispatch = useDispatch();
  const isLiveRef = useRef();
  const isPlayingRef = useRef();
  const progressRef = useRef();
  const sessionIdRef = useRef();
  const streamedCountRef = useRef(0);

  /**
   * @description Stores isLive in a ref every time it changes
   */
  useEffect(() => {
    isLiveRef.current = isLive;
  }, [isLive]);

  /**
   * @description Stores isPlaying in a ref every time it changes
   */
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  /**
   * @description Stores progress in a ref every time it changes
   */
  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  const pulse = useCallback(
    (pulseAction) => () => {
      const progress = progressRef.current;
      const sessionId = sessionIdRef.current;
      const isLive = isLiveRef.current;
      const streamedCount = Math.floor(streamedCountRef.current);

      dispatch(pulseStreamHeartbeat({ isLive, progress, pulseAction, sessionId, streamedCount }));
    },
    [dispatch]
  );

  /**
   * @description Sends initial heartbeat and starts the intervals
   * @cleanup Stops heartbeat and its intervals
   */
  useEffect(() => {
    if (!isReadyToPlay) return;

    const sendPulse = pulse(HEARTBEAT_PULSE_TYPES.SEND);
    const stopPulse = pulse(HEARTBEAT_PULSE_TYPES.STOP);

    sessionIdRef.current = `session_${generateRandomString()}`;
    streamedCountRef.current = 0;

    sendPulse();

    window.addEventListener("beforeunload", stopPulse);

    const heartbeatInterval = setInterval(sendPulse, 120000);

    const streamedCountInterval = setInterval(() => {
      if (!isPlayingRef.current) return;

      streamedCountRef.current += 1.5;
    }, 1500);

    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(streamedCountInterval);
      stopPulse();
      window.removeEventListener("beforeunload", stopPulse);
    };
  }, [dispatch, isReadyToPlay, pulse]);
  return { stopPulse: pulse(HEARTBEAT_PULSE_TYPES.STOP) };
};

/* Player data sent to Conviva */
export const PLAYER_INFO = {
  PLAYER_NAME: isBrazil ? "DirecTV Brazil Mobile Web" : "DirecTV LATAM Mobile Web",
  PLAYER_VERSION: "1.0.1",
  APP_VERSION: process.env.REACT_APP_PRIVATE_WEB_MOBILE_VERSION,
  CONVIVA_ENV: isProduction ? "Prod" : "Test",
  ORBIS_ENV: isProduction ? "Prod" : "Staging",
  VINDICIA_ENV: isProduction ? "Prod" : "Test"
};

/**
 * @method getConvivaConfig
 * @description Returns conviva config needed for the player
 */
export const getConvivaConfig = () => ({
  customerKey: process.env.REACT_APP_CONVIVA_CUSTOMER_KEY,
  isTouchstoneEnabled,
  touchstoneUrl: process.env.REACT_APP_CONVIVA_TOUCHSTONE_URL,
  traceLogs: convivaTraceLogs,
  playerInfo: PLAYER_INFO,
  deviceType: "Mobile"
});
